import { Chip } from '@heroui/react';
import classNames from 'classnames';
import type { ReactNode } from 'react';

import DynamicT from '@/shared/components/DynamicT';

/**
 * "Last used" marker for the sign-in option the user last succeeded with.
 *
 * It sits before the option as a sibling, never inside it: inside, its text would be folded
 * into the button's accessible name and it would push a centred label off-centre. As a plain
 * preceding sibling, screen readers reach it in document order with no aria wiring.
 */
// Neutral, not accent: it is a hint, and an accent pill competes with the primary action.
const LastUsedChip = ({ className }: { readonly className?: string }) => (
  <Chip size="sm" variant="soft" className={className}>
    <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />
    <Chip.Label>
      <DynamicT forKey="description.last_used" />
    </Chip.Label>
  </Chip>
);

/**
 * Wraps a full-width option and pins the marker as a tab on its top-end corner, so marking an
 * option never shifts the stack. The wrapper renders in both states and always carries the
 * caller's `className`, so parent spacing (`last:mb-0`, grid placement) keeps targeting the
 * same element.
 */
export const LastUsedFrame = ({
  isLastUsed,
  className,
  children,
}: {
  readonly isLastUsed: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}) => (
  <div className={classNames('relative flex flex-col', className)}>
    {isLastUsed && (
      // The solid page fill masks the button's border where the tab overlaps it.
      <span className="pointer-events-none absolute -top-2 end-6 z-[1] inline-flex h-4 items-center rounded-full border border-border bg-background px-1.5 text-[11px] leading-none font-medium text-muted">
        <DynamicT forKey="description.last_used" />
      </span>
    )}
    {children}
  </div>
);

export default LastUsedChip;
